// @ts-strict-ignore
import * as React from 'react';
import {PureComponent} from 'react';
import {clsx} from 'clsx';
import throttle from 'lodash/throttle';
import ResizeObserver from 'resize-observer-polyfill';

import {getChartSegmentColorCombo, rgbaStringFromHexString} from 'common/util/colors';
import {abbreviateNumber, formatAxisLabelWithUnit} from 'common/util';
import {TooltipReact} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {TooltipItemType, TooltipSection} from 'common/components/chart-tooltip/types';
import {ChartTooltip, ChartTooltipProps} from 'common/components/chart-tooltip';
import {getDownloadMode} from 'common/report/util';
import {DownloadMode} from 'common/report/constants';
import {CalendarDateRange} from 'common/widgets/date-range-picker/models/types';
import {AnalysisType, AxisUnitOptions, BookmarkYAxisOptions} from 'common/report/insights/util/types';
import {AxisAssignment} from 'common/charts/types';
import {UrlManager} from 'common/contexts/url-manager/types';
import {ScrollControlReact} from 'common/widgets/scroll-control';
import {ScrollSyncer} from 'common/util/scroll-syncer';
import type {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {getAxisUnitPrecision} from 'common/query-builder/chart-customization/utils';
import {useTranslation} from 'common/i18n/hooks';
import {IronContextRegistry} from 'common/report/context-registry';
import {displayOptionsMatchAxis} from 'common/report/insights/models/utils';

import {
  BAR_GAP_PX,
  DEFAULT_MINIMUM_STEP_WIDTH,
  MINIMUM_BAR_WIDTH_TO_SHOW_COUNT_LABELS,
  MINIMUM_CSS_BAR_HEIGHT_PERCENT,
  STEP_RIGHT_PADDING_PX,
  Y_AXIS_LOG_TICK_PERCENTS,
  Y_AXIS_TICK_PERCENTS,
} from './constants';
import {
  ConversionStep,
  ConversionStepBar,
  ConversionStepBarType,
  ConversionStepLayout,
  getCssHeightPercentLog,
  getMaxValue,
  getMinValue,
  getNumBarsPerStep,
  getPercentLabel,
  getStepGap,
  getStepWidth,
  NormalConversionStep,
} from './util';
import css from './index.module.styl?inline';
import styles from './index.module.styl';
import {ConversionColumnVizFormatter} from './types';

export type OnStepBarContextMenuArgs = {
  conversionStep: NormalConversionStep;
  barIndex: number;
  originalMouseEvent: MouseEvent;
};
export type OnStepBarClickArgs = {
  // in practice, clicking a bar is usually related to bar selection, so including the step index
  // is more helpful for configuring bar selection than including the basic ConversionStep itself
  stepIndex: number;
  color: string;
  isComparison: boolean;
  comparisonDateRange: CalendarDateRange | null;
  barIndex: number;
  isDropoff: boolean;
  originalMouseEvent: MouseEvent;
};

export enum StepHorizontalWheelDirection {
  Left = `left`,
  Right = `right`,
}

export type OnStepHorizontalWheelArgs = {
  firstVisibleStepIndex: number;
  direction: StepHorizontalWheelDirection;
};

export interface BarSelection {
  stepIndex: number;
  barIndex: number;
  isDropoff: boolean;
}

interface ConversionColumnVizState {
  horizontalScrollbarHeight: number;
  hoveredStepBar: null | {
    stepIndex: number;
    barIndex: number;
    hoveredOnDropoff: boolean;
    barElement: HTMLElement;
  };
  stepWidth: number;
}

type ConversionStepBarTooltipDataCallback = (
  conversionStep: NormalConversionStep,
  previousConversionStep: null | NormalConversionStep,
  barIndex: number,
  hoveredOnDropoff: boolean,
) => ChartTooltipProps;

export type ConversionColumnVizProps = {
  className?: string;
  conversionSteps: ConversionStep[];
  enablePopoverMenu?: boolean;
  enableBarClick?: boolean;
  formatter: ConversionColumnVizFormatter;
  minStepWidth?: number;
  maxStepWidth?: number;
  onStepBarContextMenu?: (args: OnStepBarContextMenuArgs) => void;
  onStepBarClick?: (args: OnStepBarClickArgs) => void;
  onStepHorizontalWheel?: (args: OnStepHorizontalWheelArgs) => void;
  popoverMenuOpen?: boolean;
  primaryYAxisOptions?: BookmarkYAxisOptions;
  scrollSyncer?: ScrollSyncer;
  secondaryStepLayouts: ConversionStepLayout[];
  secondaryYAxisOptions?: BookmarkYAxisOptions;
  selectedBars?: BarSelection[];
  stepBarTooltipCallback?: ConversionStepBarTooltipDataCallback;
  /**
   * Each layout corresponds to the ConversionStep of the same array index in `this.conversionSteps`
   */
  stepLayouts: ConversionStepLayout[];
  urlManager?: UrlManager;
  segmentFilterController?: SegmentFilterController;
  useAbsoluteValues?: boolean;
};

export interface ConversionColumnVizJsxProps {
  props?: ConversionColumnVizProps;
}

export class ConversionColumnVizClass extends PureComponent<
  ConversionColumnVizProps & Pick<IronContextRegistry, 'i18n'>,
  ConversionColumnVizState
> {
  public declare stepBarTooltipCallback?: ConversionStepBarTooltipDataCallback;

  private declare multiResizeObserver?: ResizeObserver;
  private declare scrollContainer?: React.RefObject<HTMLDivElement>;
  private declare stepsContainer?: React.RefObject<HTMLDivElement>;
  private declare lastScrollLeft?: number;

  static defaultProps = {
    minStepWidth: DEFAULT_MINIMUM_STEP_WIDTH,
  };

  constructor(props) {
    super(props);
    this.scrollContainer = React.createRef();
    this.stepsContainer = React.createRef();
    this.state = {
      horizontalScrollbarHeight: 0,
      hoveredStepBar: null,
      stepWidth: 0,
    };
  }

  public componentDidMount() {
    this.multiResizeObserver = new ResizeObserver(() => {
      this.lastScrollLeft = this.scrollContainer.current.scrollLeft;
      this.setState({
        horizontalScrollbarHeight:
          this.scrollContainer.current.offsetHeight - this.scrollContainer.current.clientHeight,
        stepWidth: this.getStepWidth(),
      });
    });
    this.lastScrollLeft = this.scrollContainer.current.scrollLeft;
    this.multiResizeObserver.observe(this.scrollContainer.current);
    this.multiResizeObserver.observe(this.stepsContainer.current);
    this.setState({
      stepWidth: this.getStepWidth(),
    });
    if (this.props.scrollSyncer) {
      this.props.scrollSyncer.register(this.scrollContainer.current);
    }
  }

  public componentWillUnmount() {
    this.multiResizeObserver.disconnect();
  }

  private getStepWidth(): number {
    return getStepWidth(
      this.props.conversionSteps,
      this.scrollContainer.current,
      this.props.minStepWidth,
      this.props.maxStepWidth,
    );
  }

  /**
   * key is the step index, value is a collection of selected bar indices
   * @returns
   */
  private getBarSelectionLookup(): Map<number, Set<number>> {
    const barSelectionLookup = new Map<number, Set<number>>();
    if (this.props.selectedBars) {
      for (const selection of this.props.selectedBars) {
        if (!barSelectionLookup.has(selection.stepIndex)) {
          barSelectionLookup.set(selection.stepIndex, new Set<number>());
        }
        if (!selection.isDropoff) {
          barSelectionLookup.get(selection.stepIndex).add(selection.barIndex);
        }
      }
    }
    return barSelectionLookup;
  }

  /**
   * key is the step index, value is a collection of selected bar indices
   * @returns
   */
  private getDropoffBarSelectionLookup(): Map<number, Set<number>> {
    const dropoffBarSelectionLookup = new Map<number, Set<number>>();
    if (this.props.selectedBars) {
      for (const selection of this.props.selectedBars) {
        if (!dropoffBarSelectionLookup.has(selection.stepIndex)) {
          dropoffBarSelectionLookup.set(selection.stepIndex, new Set<number>());
        }
        if (selection.isDropoff) {
          dropoffBarSelectionLookup.get(selection.stepIndex).add(selection.barIndex);
        }
      }
    }
    return dropoffBarSelectionLookup;
  }

  private renderTemplate() {
    // NOTE: This would need to be changed if we add support for more than one
    // set of funnel steps.
    const firstConversionStep = this.props.conversionSteps[0];
    const primaryAxisHasFunnelStepsAssigned =
      firstConversionStep &&
      firstConversionStep.placeholder !== true &&
      // Axis is default primary
      displayOptionsMatchAxis(firstConversionStep.display, AxisAssignment.Primary);
    const primaryAxisLabelDefined = this.props.primaryYAxisOptions?.title?.text;
    const primaryAxisShouldBeEnabled =
      // Enabled is default true
      this.props.primaryYAxisOptions?.labels?.enabled !== false &&
      (primaryAxisLabelDefined || primaryAxisHasFunnelStepsAssigned);
    const secondaryAxisHasFunnelStepsAssigned =
      firstConversionStep &&
      firstConversionStep.placeholder !== true &&
      displayOptionsMatchAxis(firstConversionStep.display, AxisAssignment.Secondary);
    const secondaryAxisLabelDefined = this.props.secondaryYAxisOptions?.title?.text;
    const secondaryAxisShouldBeEnabled =
      this.props.secondaryYAxisOptions?.labels?.enabled !== false &&
      (secondaryAxisLabelDefined || secondaryAxisHasFunnelStepsAssigned);

    return (
      <div
        className={clsx(styles.root, {[styles.barLabelGap]: this.showBarLabels()}, this.props.className)}
        style={{'--horizontal-scrollbar-height': `${this.state.horizontalScrollbarHeight}px`}}
        elref='conversionColumnViz'
      >
        <style>{css}</style>
        {primaryAxisShouldBeEnabled && (
          <div className={styles.primaryYAxis} elref='primaryAxis'>
            {primaryAxisLabelDefined && (
              <div className={styles.primaryYAxisTitle} style={{gridColumn: 1}}>
                {this.props.primaryYAxisOptions?.title?.text}
              </div>
            )}
            {primaryAxisHasFunnelStepsAssigned && (
              <div className={styles.yAxisLabels} style={{gridColumn: 2}}>
                {this.getYAxisLabels(this.props.primaryYAxisOptions).map((label, i) => (
                  <React.Fragment key={i}>
                    <div className={styles.label} style={{bottom: `${Y_AXIS_TICK_PERCENTS[i]}%`}}>
                      {label}
                    </div>
                    <div aria-hidden='true' className={styles.spaceFillingLabel}>
                      {label}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}
        {this.renderScrollableContent()}
        {secondaryAxisShouldBeEnabled && (
          <div className={styles.secondaryYAxis} elref='secondaryAxis'>
            {secondaryAxisHasFunnelStepsAssigned && (
              <div className={styles.yAxisLabels} style={{gridColumn: 1}}>
                {this.getYAxisLabels(this.props.secondaryYAxisOptions).map((label, i) => (
                  <React.Fragment key={i}>
                    <div className={styles.label} style={{bottom: `${Y_AXIS_TICK_PERCENTS[i]}%`}}>
                      {label}
                    </div>
                    <div aria-hidden='true' className={styles.spaceFillingLabel}>
                      {label}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
            {secondaryAxisLabelDefined && (
              <div className={styles.secondaryYAxisTitle} style={{gridColumn: 2}}>
                {this.props.secondaryYAxisOptions?.title?.text}
              </div>
            )}
          </div>
        )}

        {this.renderBarTooltip()}
      </div>
    );
  }

  private renderScrollableContent() {
    const isDownloadMode = getDownloadMode(this.props.urlManager) !== DownloadMode.None;
    return (
      <div
        className={clsx(styles.scrollContainer, {[styles.disableHorizontalScroll]: isDownloadMode})}
        ref={this.scrollContainer}
        onWheel={(event) => this.handleScrollContainerWheel(event as TODO)}
      >
        <ScrollControlReact
          mousemoveEl={this.scrollContainer?.current}
          scrollBy={this.state.stepWidth}
          scrollEl={this.scrollContainer?.current}
        />
        <div
          className={styles.steps}
          ref={this.stepsContainer}
          style={{
            gridTemplateColumns: `repeat(${this.props.conversionSteps.length}, ${this.state.stepWidth}px)`,
          }}
        >
          <div className={styles.gridlines} style={{gridColumn: `1 / span ${this.props.conversionSteps.length}`}}>
            {Y_AXIS_TICK_PERCENTS.map((percent, index) => (
              <div className={styles.yAxisGridline} key={index} style={{bottom: `${percent}%`}} />
            ))}
          </div>

          {this.props.conversionSteps.map((step, stepIndex) => {
            if (step.placeholder === true) {
              return (
                <div
                  className={styles.stepLabel}
                  key={stepIndex}
                  style={{
                    gridColumn: `${stepIndex + 1}`,
                    paddingLeft: `${getStepGap(this.props.conversionSteps) / 2}px`,
                  }}
                >
                  -
                </div>
              );
            }

            const stepAxisAssignment = step.display?.axis || AxisAssignment.Primary;
            const stepAxisUnitOptions =
              stepAxisAssignment === AxisAssignment.Primary
                ? this.props.primaryYAxisOptions?.axisUnit
                : this.props.secondaryYAxisOptions?.axisUnit;
            const stepLayout = this.props.stepLayouts[stepIndex];
            const secondaryStepLayout = this.props.secondaryStepLayouts[stepIndex];
            return (
              <React.Fragment key={stepIndex}>
                {/**
                 * Each bar group is an independent grid where each bar's width is "1fr".
                 * This allows us to configure each conversion step's group of bars as a whole where each bar's width is managed by CSS grid,
                 * this should be more precise and responsive than configuring each bar's width and then adding it up to a required group width.
                 * In some cases, we require a specific width for each whole bar group to align with a sibling widget (eg. a table's columns).
                 **/}
                <div
                  className={styles.barGroup}
                  elref={`barGroup`}
                  style={{
                    columnGap: `${BAR_GAP_PX}px`,
                    gridColumn: `${stepIndex + 1}`,
                    paddingLeft: `${getStepGap(this.props.conversionSteps) / 2}px`,
                    paddingRight: `${getStepGap(this.props.conversionSteps) / 2 + STEP_RIGHT_PADDING_PX}px`,
                  }}
                >
                  {step.bars.map((bar, barIndex) => {
                    const barLayout = stepLayout.barLayouts[barIndex];
                    const secondaryBarLayout = secondaryStepLayout.barLayouts[barIndex];
                    const barType = bar.type ?? ConversionStepBarType.Normal;
                    const barColor =
                      bar.color ??
                      getChartSegmentColorCombo(barIndex, this.props.segmentFilterController.getThemeColors()).primary;

                    const heightPercent =
                      stepAxisAssignment === AxisAssignment.Secondary
                        ? this.props.secondaryYAxisOptions?.type === AnalysisType.Logarithmic
                          ? getCssHeightPercentLog(
                              secondaryBarLayout.heightPercent,
                              this.props.secondaryYAxisOptions?.min,
                              this.props.secondaryYAxisOptions?.max,
                            )
                          : secondaryBarLayout.heightPercent * 100
                        : this.props.primaryYAxisOptions?.type === AnalysisType.Logarithmic
                          ? getCssHeightPercentLog(
                              barLayout.heightPercent,
                              this.props.primaryYAxisOptions?.min,
                              this.props.primaryYAxisOptions?.max,
                            )
                          : barLayout.heightPercent * 100;

                    // The rendered CSS bar heights must be clamped to a minimum height for UX reasons.
                    // Heights are clamped during render instead of during layout generation so that layout generation logic can be simpler for testing.
                    const cssHeightPercent = Math.max(MINIMUM_CSS_BAR_HEIGHT_PERCENT, heightPercent);
                    const cssHeightPercentClampAmount = (cssHeightPercent - heightPercent) / 100;

                    const dropoffHeightPercent =
                      stepAxisAssignment === AxisAssignment.Secondary
                        ? secondaryBarLayout.dropoffHeightPercent
                        : barLayout.dropoffHeightPercent;
                    // We need to compensate for any clamping on the height of the main bar
                    const cssDropoffHeightPercent = Math.min(
                      100 - cssHeightPercent,
                      Math.max(
                        MINIMUM_CSS_BAR_HEIGHT_PERCENT,
                        (dropoffHeightPercent - cssHeightPercentClampAmount) * 100,
                      ),
                    );

                    const virtualHoverBarHeightPercent = cssHeightPercent + cssDropoffHeightPercent;

                    const hoveredSeriesId = this.props.segmentFilterController?.hoveredSeries?.seriesId;

                    return (
                      <div
                        className={clsx(styles.barColumn, {
                          [styles.popoverMenuEnabled]: this.props.enablePopoverMenu,
                          [styles.virtualHover]: hoveredSeriesId && bar.extras === hoveredSeriesId,
                        })}
                        key={barIndex}
                        elref={`barColumn`}
                        style={{
                          '--bar-color': barColor,
                          gridColumn: `${barIndex + 1}`,
                        }}
                        onClick={
                          this.props.enablePopoverMenu
                            ? (ev) => this.handleConversionStepContextMenu(ev as TODO, step, barIndex)
                            : null
                        }
                      >
                        <div className={styles.virtualHoverBar} style={{height: `${virtualHoverBarHeightPercent}%`}} />
                        {bar.conversionRate < 1 && bar.conversionRate > 0 && (
                          <div
                            className={clsx(styles.dropoffBar, styles.roundedTop, {
                              [styles.selected]:
                                this.getDropoffBarSelectionLookup().get(stepIndex)?.has(barIndex) ?? false,
                              [styles.clickEnabled]: this.props.enableBarClick,
                              [styles.comparisonType]: barType === ConversionStepBarType.Comparison,
                            })}
                            elref={`dropoffBar`}
                            style={{
                              '--selected-gradient': `linear-gradient(to bottom, ${barColor} 0%, ${rgbaStringFromHexString(
                                barColor,
                                0.05,
                              )} 100%)`,
                              // faded gradient is equivalent to 30% opacity of the selected gradient
                              // opacity is applied manually instead of using the "opacity" CSS property because we don't want to apply opacity to the :before pseudoelement
                              '--faded-gradient': `linear-gradient(to bottom, ${rgbaStringFromHexString(
                                barColor,
                                0.3,
                              )} 0%, ${rgbaStringFromHexString(barColor, 0.015)} 100%)`,
                              bottom: `${cssHeightPercent}%`,
                              height: `${cssDropoffHeightPercent}%`,
                            }}
                            onClick={
                              this.props.enableBarClick
                                ? (ev) =>
                                    this.handleConversionStepBarClick(ev as TODO, {
                                      isComparison: barType === ConversionStepBarType.Comparison,
                                      stepIndex,
                                      barIndex,
                                      isDropoff: true,
                                      color: barColor,
                                    })
                                : null
                            }
                            onMouseLeave={() => this.setState({hoveredStepBar: null})}
                            onMouseMove={(ev) => {
                              const hoveredStepBar = {
                                stepIndex,
                                barIndex,
                                barElement: ev.currentTarget as HTMLElement,
                                hoveredOnDropoff: true,
                              };
                              this.setState({hoveredStepBar});
                            }}
                          />
                        )}
                        <div
                          className={clsx(styles.conversionBar, {
                            [styles.selected]: this.getBarSelectionLookup().get(stepIndex)?.has(barIndex) ?? false,
                            [styles.clickEnabled]: this.props.enableBarClick,
                            [styles.roundedTop]: bar.conversionRate >= 1,
                            [styles.comparisonType]: barType === ConversionStepBarType.Comparison,
                          })}
                          elref={`conversionBar`}
                          style={{
                            height: `${cssHeightPercent}%`,
                          }}
                          onClick={
                            this.props.enableBarClick
                              ? (ev) =>
                                  this.handleConversionStepBarClick(ev as TODO, {
                                    isComparison: barType === ConversionStepBarType.Comparison,
                                    stepIndex,
                                    barIndex,
                                    isDropoff: false,
                                    color: barColor,
                                  })
                              : null
                          }
                          onMouseLeave={() => this.setState({hoveredStepBar: null})}
                          onMouseMove={(ev) => {
                            const hoveredStepBar = {
                              stepIndex,
                              barIndex,
                              barElement: ev.currentTarget as HTMLElement,
                              hoveredOnDropoff: false,
                            };
                            this.setState({hoveredStepBar});
                          }}
                        />
                        {this.showBarLabels() ? (
                          <div className={styles.barCountLabel} style={{bottom: `${cssHeightPercent}%`}}>
                            <div>{this.getTopLabelValue(bar, stepAxisUnitOptions)}</div>
                            <div className={styles.value}>{this.getBottomLabelValue(bar, stepAxisUnitOptions)}</div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <div
                  className={styles.stepLabel}
                  style={{
                    gridColumn: `${stepIndex + 1}`,
                    paddingLeft: `${getStepGap(this.props.conversionSteps) / 2}px`,
                  }}
                  title={step.tooltipName || step.displayName}
                >
                  <span className={styles.stepNumber}>{stepIndex + 1}</span>
                  {step.displayName}

                  <TooltipReact maxWidth={`300px`} offset={0} placement={Placement.Top}>
                    {step.tooltipName || step.displayName}
                  </TooltipReact>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  private renderBarTooltip() {
    const isPopoverMenuOpen = this.props.popoverMenuOpen;
    const hoveredStepBar = this.state.hoveredStepBar;
    if (hoveredStepBar && !isPopoverMenuOpen) {
      const conversionStep = this.props.conversionSteps[hoveredStepBar.stepIndex] as NormalConversionStep;
      const previousConversionStep: null | NormalConversionStep = (() => {
        for (let i = Math.max(0, hoveredStepBar.stepIndex - 1); i >= 0; i--) {
          if (this.props.conversionSteps[i].placeholder !== true) {
            return this.props.conversionSteps[i] as NormalConversionStep;
          }
        }
        return null;
      })();

      const tooltipCallback: ConversionStepBarTooltipDataCallback =
        this.props.stepBarTooltipCallback ?? this.getDefaultTooltipData.bind(this);
      const tooltipParams = tooltipCallback(
        conversionStep,
        previousConversionStep,
        hoveredStepBar.barIndex,
        hoveredStepBar.hoveredOnDropoff,
      );

      if (tooltipParams.sections.length > 0) {
        return (
          <ChartTooltip
            className={styles.tooltip}
            {...{...tooltipParams, positioning: {positionParent: hoveredStepBar.barElement}, renderInPortal: true}}
          />
        );
      }
    }
    return null;
  }

  private getDefaultTooltipData(
    conversionStep: NormalConversionStep,
    previousConversionStep: null | NormalConversionStep,
    barIndex: number,
    hoveredOnDropoff: boolean,
  ): ChartTooltipProps {
    const bar = conversionStep.bars[barIndex];
    const title: string = (() => {
      if (bar.displayName) {
        return bar.displayName;
      }
      if (conversionStep.bars.length === 1) {
        return conversionStep.tooltipName ?? conversionStep.displayName;
      }
      return ``;
    })();
    const {t} = this.props.i18n;

    const percent = hoveredOnDropoff ? 1 - bar.conversionRate : bar.conversionRate;
    const percentDescription = hoveredOnDropoff
      ? t(`chart-actions.tooltip.dropped-off-users`)
      : t(`chart-actions.tooltip.converted-users`);

    const dropoffValue = previousConversionStep
      ? previousConversionStep.bars[barIndex].value - conversionStep.bars[barIndex].value
      : 0;
    const count = hoveredOnDropoff ? dropoffValue : conversionStep.bars[barIndex].value;

    const tooltipSections: TooltipSection[] = [
      {
        rows: [{items: [{text: title, type: TooltipItemType.Strong}]}],
      },
      {
        rows: [
          {
            items: [
              {text: getPercentLabel(percent), type: TooltipItemType.Data},
              {text: percentDescription, type: TooltipItemType.Text},
            ],
          },
          {
            items: [
              {text: count.toString(), type: TooltipItemType.Data},
              {text: t(`chart-actions.tooltip.totals`), type: TooltipItemType.Text},
            ],
          },
        ],
      },
    ];

    const barColor = conversionStep.bars[barIndex].color ?? getChartSegmentColorCombo(barIndex).primary;

    return {sections: tooltipSections, titleColor: barColor};
  }

  private getEstimatedBarWidth(): number {
    const stepWidth = this.state.stepWidth;
    const barsPerStep = getNumBarsPerStep(this.props.conversionSteps);

    if (barsPerStep <= 0) {
      return 0;
    }

    // Note: this is only an estimate of the bar width for showing/hiding certain UI elements,
    //       each bar's true width is controlled by CSS grid sizing instead of JS
    return (
      (stepWidth - getStepGap(this.props.conversionSteps) - STEP_RIGHT_PADDING_PX - (barsPerStep - 1) * BAR_GAP_PX) /
      barsPerStep
    );
  }

  private showBarLabels(): boolean {
    return this.getEstimatedBarWidth() >= MINIMUM_BAR_WIDTH_TO_SHOW_COUNT_LABELS;
  }

  private handleConversionStepContextMenu(
    ev: MouseEvent,
    conversionStep: NormalConversionStep,
    barIndex: number,
  ): void {
    ev.preventDefault();
    ev.stopPropagation();
    if (this.props.onStepBarContextMenu) {
      this.props.onStepBarContextMenu({
        conversionStep,
        barIndex,
        originalMouseEvent: ev,
      });
    }
  }

  private handleConversionStepBarClick(
    ev: MouseEvent,
    {
      isComparison,
      stepIndex,
      barIndex,
      isDropoff,
      color,
    }: {
      isComparison: boolean;
      color: string;
      stepIndex: number;
      barIndex: number;
      isDropoff: boolean;
    },
  ): void {
    if (this.props.onStepBarClick) {
      this.props.onStepBarClick({
        stepIndex,
        color,
        isComparison,
        comparisonDateRange: null,
        barIndex,
        isDropoff,
        originalMouseEvent: ev,
      });
    }
  }

  private handleScrollContainerWheel(_: WheelEvent): void {
    const scrollContainer = this.scrollContainer?.current;
    if (scrollContainer) {
      const scrollLeft = scrollContainer.scrollLeft;
      // we need to calculate our own deltaX instead of using `ev.deltaX` because the "wheel" event behavior varies across browsers
      // and its delta values don't necessarily reflect the content's scrolling direction
      const deltaX = scrollLeft - this.lastScrollLeft;
      this.lastScrollLeft = scrollLeft;

      if (deltaX !== 0) {
        this.throttledDispatchStepHorizontalScrollEvent(deltaX);
      }
    }
  }

  private throttledDispatchStepHorizontalScrollEvent: (deltaX: number) => void = throttle((deltaX: number): void => {
    const stepWidth = this.state.stepWidth;
    const firstVisibleStepIndex = Math.round(this.lastScrollLeft / stepWidth);
    if (this.props.onStepHorizontalWheel) {
      this.props.onStepHorizontalWheel({
        firstVisibleStepIndex,
        direction: deltaX > 0 ? StepHorizontalWheelDirection.Right : StepHorizontalWheelDirection.Left,
      });
    }
  }, 500);

  private getYAxisLabelFromPercent(percent: number, yAxisOptions: BookmarkYAxisOptions): string {
    const precision = getAxisUnitPrecision(yAxisOptions?.axisUnit);
    const min = getMinValue(this.props.conversionSteps, yAxisOptions);
    const max = getMaxValue(this.props.conversionSteps, yAxisOptions);
    if (this.props.useAbsoluteValues) {
      return `${abbreviateNumber(min + ((max - min) * percent) / 100.0, {
        precision,
        removeDecimalZero: precision === undefined,
      })}`;
    }
    // For percents we'll accept values in the interval [0, 1]
    const percentMax = Math.min(max, 1);
    const relativePercent = min + (percent / 100.0) * (percentMax - min);
    return `${abbreviateNumber(relativePercent * 100.0, {precision, removeDecimalZero: precision === undefined})}`;
  }

  public getYAxisLabels(yAxisOptions: BookmarkYAxisOptions): string[] {
    const ticks = yAxisOptions?.type === AnalysisType.Logarithmic ? Y_AXIS_LOG_TICK_PERCENTS : Y_AXIS_TICK_PERCENTS;
    return ticks.map((percent) =>
      formatAxisLabelWithUnit(
        this.getYAxisLabelFromPercent(percent, yAxisOptions),
        yAxisOptions?.axisUnit?.prefix,
        yAxisOptions?.axisUnit?.suffix || (this.props.useAbsoluteValues ? `` : `%`),
      ),
    );
  }

  private getTopLabelValue(bar: ConversionStepBar, axisUnitOptions: AxisUnitOptions) {
    return this.props.useAbsoluteValues
      ? this.props.formatter(bar.value, false, axisUnitOptions)
      : this.props.formatter(bar.conversionRate, true, axisUnitOptions);
  }

  private getBottomLabelValue(bar: ConversionStepBar, axisUnitOptions: AxisUnitOptions) {
    return this.props.useAbsoluteValues
      ? this.props.formatter(bar.conversionRate, true, axisUnitOptions)
      : this.props.formatter(bar.value, false, axisUnitOptions);
  }

  render() {
    return this.renderTemplate();
  }
}

export const ConversionColumnViz = React.forwardRef<ConversionColumnVizClass, ConversionColumnVizProps>(
  (props, ref) => {
    const i18n = useTranslation();
    return <ConversionColumnVizClass ref={ref} i18n={i18n} {...props} />;
  },
);
